import React from "react";
import {
  Card,
  Input,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  DatePicker,
  Upload,
  Button,
  Space,
  Switch,
  Tooltip,
  Flex,
  Typography,
  Popconfirm,
  Slider,
  Table,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  FIELD_TYPES,
  MAX_NESTING_DEPTH,
  newField,
  newOption,
  newTableColumn,
} from "../../../../../../domain/clinicalForms";
import { FIELD_TYPE_OPTIONS } from "../ui";
import { useFieldError } from "./ValidationContext";

const ERROR_COLOR = "#ff4d4f";
const FIELD_BORDER = "#d0d5dd";

const safeTrim = (value) => (value || "").trim();

const InlineError = ({ message, indent = 0 }) =>
  message ? (
    <Typography.Text
      type="danger"
      style={{
        fontSize: 12,
        display: "block",
        marginLeft: indent,
        marginTop: 2,
      }}
    >
      {message}
    </Typography.Text>
  ) : null;

const NestedFieldList = ({ fields, onChange, depth, basePath = [] }) => {
  const update = (id, next) =>
    onChange(fields.map((f) => (f.id === id ? next : f)));
  const remove = (id) => onChange(fields.filter((f) => f.id !== id));
  const duplicate = (id) => {
    const target = fields.find((f) => f.id === id);
    if (!target) return;
    const copy = { ...target, id: `${target.id}_copy_${Date.now()}` };
    const idx = fields.findIndex((f) => f.id === id);
    const next = fields.slice();
    next.splice(idx + 1, 0, copy);
    onChange(next);
  };

  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      {fields.map((f, idx) => (
        <FieldRow
          key={f.id}
          field={f}
          depth={depth}
          basePath={[...basePath, idx]}
          onChange={(next) => update(f.id, next)}
          onDelete={() => remove(f.id)}
          onDuplicate={() => duplicate(f.id)}
        />
      ))}
    </Space>
  );
};

const OptionRow = ({
  option,
  Control,
  onChange,
  onRemove,
  canAddSub,
  depth,
  basePath = [],
}) => {
  const labelError = useFieldError([...basePath, "label"]);
  const labelMissing = !safeTrim(option.label);

  const updateSubs = (subs) => onChange({ ...option, sub_questions: subs });
  const addSub = () =>
    updateSubs([...(option.sub_questions || []), newField()]);

  return (
    <div>
      <Space style={{ width: "100%" }} align="center">
        <Control disabled />
        <Input
          value={option.label}
          variant="borderless"
          status={labelError.error ? "error" : undefined}
          onChange={(e) => {
            if (labelError.error) labelError.clear();
            onChange({ ...option, label: e.target.value });
          }}
          placeholder="Option label"
          style={{ minWidth: 180 }}
        />
        {canAddSub && (
          <Tooltip
            title={
              labelMissing
                ? "Enter the option label first"
                : "Add sub-question to this option"
            }
          >
            <span>
              <Button
                size="small"
                type="text"
                icon={<PlusOutlined />}
                onClick={addSub}
                disabled={labelMissing}
              />
            </span>
          </Tooltip>
        )}
        <Popconfirm
          title="Delete this option?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={onRemove}
        >
          <Button type="text" size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
      <InlineError message={labelError.error} indent={28} />
      {option.sub_questions?.length > 0 && (
        <div
          style={{
            marginLeft: 36,
            marginTop: 8,
            paddingLeft: 20,
            borderLeft: "2px solid var(--drf-primary-border)",
          }}
        >
          <NestedFieldList
            fields={option.sub_questions}
            onChange={updateSubs}
            depth={depth + 1}
            basePath={[...basePath, "sub_questions"]}
          />
        </div>
      )}
    </div>
  );
};

const ChoiceOptions = ({ field, onChange, multiple, depth, basePath = [] }) => {
  const optionsError = useFieldError([...basePath, "options"]);
  const Control = multiple ? Checkbox : Radio;
  const update = (idx, next) => {
    const arr = field.options.slice();
    arr[idx] = next;
    onChange({ ...field, options: arr });
  };
  const remove = (idx) => {
    if (field.options.length <= 1) return;
    onChange({ ...field, options: field.options.filter((_, i) => i !== idx) });
  };
  const add = () =>
    onChange({
      ...field,
      options: [
        ...field.options,
        newOption(`Option ${field.options.length + 1}`),
      ],
    });
  const canAddSub = depth + 1 < MAX_NESTING_DEPTH;
  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      {field.options.map((opt, idx) => (
        <OptionRow
          key={opt.value || idx}
          option={opt}
          Control={Control}
          canAddSub={canAddSub}
          depth={depth}
          basePath={[...basePath, "options", idx]}
          onChange={(next) => update(idx, next)}
          onRemove={() => remove(idx)}
        />
      ))}
      <InlineError message={optionsError.error} />
      <Button type="link" icon={<PlusOutlined />} onClick={add} style={{ padding: 0 }}>
        Add option
      </Button>
    </Space>
  );
};

const SliderEditor = ({ field, onChange }) => {
  const min = field.min ?? 0;
  const max = field.max ?? 10;
  const step = field.step ?? 1;
  const LabeledNumber = ({ label, hint, value, onValueChange, ...rest }) => (
    <Flex vertical gap={2} style={{ minWidth: 110 }}>
      <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
        {label}
      </Typography.Text>
      <InputNumber
        value={value}
        onChange={onValueChange}
        style={{ width: 110 }}
        {...rest}
      />
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {hint}
      </Typography.Text>
    </Flex>
  );
  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      <Slider min={min} max={max} step={step} defaultValue={min} disabled />
      <Flex gap={16} wrap>
        <LabeledNumber
          label="Min"
          hint="Lowest value"
          value={field.min}
          onValueChange={(v) => onChange({ ...field, min: v })}
        />
        <LabeledNumber
          label="Max"
          hint="Highest value"
          value={field.max}
          onValueChange={(v) => onChange({ ...field, max: v })}
        />
        <LabeledNumber
          label="Step"
          hint="Increment size"
          value={field.step}
          min={0}
          onValueChange={(v) => onChange({ ...field, step: v })}
        />
      </Flex>
    </Space>
  );
};

const COLUMN_TYPE_OPTIONS = [
  { label: "Text", value: "text" },
  { label: "Long text", value: "textarea" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
  { label: "Date & Time", value: "datetime" },
  { label: "Dropdown", value: "select" },
  { label: "Dropdown (multi)", value: "multiselect" },
  { label: "Checkbox", value: "checkbox" },
  { label: "Radio", value: "radio" },
  { label: "File", value: "file" },
];

const COLUMN_SELECTABLE_TYPES = ["select", "multiselect", "checkbox", "radio"];

const ColumnOptionsEditor = ({ col, onChange }) => {
  const options = col.options || [];
  const updateOpt = (idx, label) => {
    const next = options.slice();
    next[idx] = { ...next[idx], label };
    onChange(next);
  };
  const removeOpt = (idx) => {
    if (options.length <= 1) return;
    onChange(options.filter((_, i) => i !== idx));
  };
  const addOpt = () =>
    onChange([...options, newOption(`Option ${options.length + 1}`)]);

  return (
    <div
      style={{
        border: `1px dashed ${FIELD_BORDER}`,
        borderRadius: 6,
        padding: 8,
        background: "#fafafa",
      }}
    >
      <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
        Options for “{col.label || "column"}”
      </Typography.Text>
      <Flex gap={6} wrap align="center" style={{ marginTop: 6 }}>
        {options.map((opt, idx) => (
          <Flex
            key={opt.value}
            align="center"
            gap={2}
            style={{
              border: `1px solid ${FIELD_BORDER}`,
              borderRadius: 4,
              padding: "1px 4px",
              background: "#ffffff",
            }}
          >
            <Input
              value={opt.label}
              variant="borderless"
              placeholder="Option"
              onChange={(e) => updateOpt(idx, e.target.value)}
              style={{ width: 120 }}
            />
            <Popconfirm
              title="Delete option?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeOpt(idx)}
              disabled={options.length <= 1}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                disabled={options.length <= 1}
              />
            </Popconfirm>
          </Flex>
        ))}
        <Button
          type="link"
          icon={<PlusOutlined />}
          onClick={addOpt}
          style={{ padding: 0 }}
        >
          Add option
        </Button>
      </Flex>
    </div>
  );
};

const TableEditor = ({ field, onChange }) => {
  const cols = field.columns || [];
  const minRows = field.min_rows ?? 0;
  const maxRows = field.max_rows ?? 50;
  const allowAddRows = field.allow_add_rows !== false;

  const updateColLabel = (idx, label) => {
    const next = cols.slice();
    next[idx] = { ...next[idx], label };
    onChange({ ...field, columns: next });
  };
  const updateColType = (idx, type) => {
    const next = cols.slice();
    const existing = next[idx];
    const needsOptions = COLUMN_SELECTABLE_TYPES.includes(type);
    next[idx] = {
      ...existing,
      type,
      options: needsOptions
        ? existing.options?.length
          ? existing.options
          : [newOption("Option 1")]
        : undefined,
    };
    onChange({ ...field, columns: next });
  };
  const updateColOptions = (idx, options) => {
    const next = cols.slice();
    next[idx] = { ...next[idx], options };
    onChange({ ...field, columns: next });
  };
  const removeCol = (idx) => {
    if (cols.length <= 1) return;
    onChange({ ...field, columns: cols.filter((_, i) => i !== idx) });
  };
  const addCol = () =>
    onChange({
      ...field,
      columns: [
        ...cols,
        newTableColumn(`Column ${cols.length + 1}`, "text"),
      ],
    });

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Columns
        </Typography.Text>
        <Flex
          gap={8}
          wrap
          align="center"
          style={{ width: "100%", marginTop: 6 }}
        >
          {cols.map((col, idx) => (
            <Flex
              key={col.id}
              align="center"
              gap={4}
              style={{
                border: `1px dashed ${FIELD_BORDER}`,
                borderRadius: 6,
                padding: "2px 6px",
                background: "#fafafa",
              }}
            >
              <Input
                value={col.label}
                variant="borderless"
                placeholder="Column label"
                onChange={(e) => updateColLabel(idx, e.target.value)}
                style={{ width: 130 }}
              />
              <Select
                size="small"
                value={col.type || "text"}
                onChange={(v) => updateColType(idx, v)}
                options={COLUMN_TYPE_OPTIONS}
                style={{ width: 110 }}
              />
              <Popconfirm
                title="Delete this column?"
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => removeCol(idx)}
                disabled={cols.length <= 1}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={cols.length <= 1}
                />
              </Popconfirm>
            </Flex>
          ))}
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={addCol}
            style={{ padding: 0 }}
          >
            Add column
          </Button>
        </Flex>
      </div>

      {cols.map((col, idx) =>
        COLUMN_SELECTABLE_TYPES.includes(col.type) ? (
          <ColumnOptionsEditor
            key={col.id}
            col={col}
            onChange={(opts) => updateColOptions(idx, opts)}
          />
        ) : null
      )}

      <Flex gap={16} wrap align="center">
        <Flex vertical gap={2}>
          <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
            Min rows
          </Typography.Text>
          <InputNumber
            min={0}
            value={minRows}
            onChange={(v) => onChange({ ...field, min_rows: v ?? 0 })}
            style={{ width: 90 }}
          />
        </Flex>
        <Flex vertical gap={2}>
          <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
            Max rows
          </Typography.Text>
          <InputNumber
            min={1}
            value={maxRows}
            onChange={(v) => onChange({ ...field, max_rows: v ?? 1 })}
            style={{ width: 90 }}
          />
        </Flex>
        <Flex align="center" gap={6}>
          <Switch
            size="small"
            checked={allowAddRows}
            onChange={(checked) =>
              onChange({ ...field, allow_add_rows: checked })
            }
          />
          <Typography.Text style={{ fontSize: 12 }}>
            Allow users to add rows
          </Typography.Text>
        </Flex>
      </Flex>

      <Table
        size="small"
        bordered
        pagination={false}
        dataSource={[{ key: "sample" }]}
        columns={cols.map((col) => ({
          title: col.label,
          dataIndex: col.id,
          key: col.id,
          width: 90,
          render: () => (
            <Input
              size="small"
              disabled
              placeholder={col.type === "number" ? "0" : "Value"}
            />
          ),
        }))}
        tableLayout="fixed"
        scroll={{ x: cols.length * 90 }}
      />
    </Space>
  );
};

const TypeBody = ({ field, onChange, depth, basePath = [] }) => {
  switch (field.type) {
    case FIELD_TYPES.TEXT:
      return <Input disabled variant="borderless" placeholder={field.placeholder || "Short answer"} />;
    case FIELD_TYPES.TEXTAREA:
      return (
        <Input.TextArea
          disabled
          autoSize={{ minRows: 2 }}
        />
      );
    case FIELD_TYPES.NUMBER:
      return (
        <Flex gap={8} wrap>
          <InputNumber
            disabled
            placeholder={field.placeholder || "0"}
            style={{ width: 160 }}
          />
          <InputNumber
            placeholder="Min"
            value={field.min}
            onChange={(v) => onChange({ ...field, min: v })}
            style={{ width: 100 }}
          />
          <InputNumber
            placeholder="Max"
            value={field.max}
            onChange={(v) => onChange({ ...field, max: v })}
            style={{ width: 100 }}
          />
        </Flex>
      );
    case FIELD_TYPES.DATE:
      return (
        <DatePicker
          disabled
          placeholder={field.placeholder || "Select date"}
          style={{ width: "100%" }}
        />
      );
    case FIELD_TYPES.DATETIME:
      return (
        <DatePicker
          showTime
          disabled
          placeholder={field.placeholder || "Select date & time"}
          style={{ width: "100%" }}
        />
      );
    case FIELD_TYPES.SELECT:
      return (
        <ChoiceOptions field={field} onChange={onChange} depth={depth} basePath={basePath} />
      );
    case FIELD_TYPES.MULTISELECT:
      return (
        <ChoiceOptions field={field} onChange={onChange} multiple depth={depth} basePath={basePath} />
      );
    case FIELD_TYPES.RADIO:
      return (
        <ChoiceOptions field={field} onChange={onChange} depth={depth} basePath={basePath} />
      );
    case FIELD_TYPES.CHECKBOX:
      return (
        <ChoiceOptions field={field} onChange={onChange} multiple depth={depth} basePath={basePath} />
      );
    case FIELD_TYPES.SLIDER:
      return <SliderEditor field={field} onChange={onChange} />;
    case FIELD_TYPES.TABLE:
      return <TableEditor field={field} onChange={onChange} />;
    case FIELD_TYPES.FILE:
      return (
        <Upload disabled>
          <Button icon={<UploadOutlined />} disabled>
            Upload file
          </Button>
        </Upload>
      );
    default:
      return null;
  }
};

const PLACEHOLDER_TYPES = [
  FIELD_TYPES.TEXT,
  FIELD_TYPES.NUMBER,
  FIELD_TYPES.SELECT,
  FIELD_TYPES.MULTISELECT,
];

const FieldRow = ({
  field,
  onChange,
  onDuplicate,
  onDelete,
  depth = 0,
  basePath = [],
}) => {
  const labelError = useFieldError([...basePath, "label"]);
  const minError = useFieldError([...basePath, "min"]);
  const supportsPlaceholder = PLACEHOLDER_TYPES.includes(field.type);

  return (
    <Card
      size="small"
      styles={{ body: { padding: 12 } }}
      style={{
        border: `1px solid ${labelError.error ? ERROR_COLOR : FIELD_BORDER}`,
        background: "#ffffff",
      }}
    >
      <Space style={{ width: "100%" }} align="center">
        <HolderOutlined style={{ color: FIELD_BORDER }} />
        <Input
          variant="borderless"
          placeholder="Question label"
          value={field.label}
          status={labelError.error ? "error" : undefined}
          onChange={(e) => {
            if (labelError.error) labelError.clear();
            onChange({ ...field, label: e.target.value });
          }}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          size="small"
          value={field.type}
          options={FIELD_TYPE_OPTIONS}
          onChange={(value) =>
            onChange({
              ...newField(value),
              id: field.id,
              label: field.label,
              required: field.required,
            })
          }
          style={{ minWidth: 160 }}
        />
        <Tooltip title="Required">
          <Switch
            size="small"
            checked={!!field.required}
            onChange={(checked) => onChange({ ...field, required: checked })}
          />
        </Tooltip>
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={onDuplicate} />
        <Popconfirm
          title="Delete this field?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={onDelete}
        >
          <Button type="text" size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
      <InlineError message={labelError.error} indent={24} />

      {supportsPlaceholder && (
        <Flex style={{ paddingLeft: 24, paddingTop: 6 }} align="center" gap={6}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Placeholder:
          </Typography.Text>
          <Input
            size="small"
            variant="borderless"
            placeholder="Optional"
            value={field.placeholder || ""}
            onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
            style={{ maxWidth: 240 }}
          />
        </Flex>
      )}

      <div style={{ paddingLeft: 24, paddingTop: 10 }}>
        <TypeBody
          field={field}
          onChange={onChange}
          depth={depth}
          basePath={basePath}
        />
        <InlineError message={minError.error} />
      </div>
    </Card>
  );
};

export default FieldRow;
